// user_notice_receipts server contract (RD-NOTICES-V2).
//
// Every "I've seen / dismissed / snoozed this" receipt used to live only in
// localStorage — per BROWSER, not per user, so the same admin signed in on
// five devices got re-greeted by the same modals on every one of them.
// Receipts are now per USER, saved server-side, and live-synced across that
// user's open sessions. localStorage stays as a fast local cache (first
// paint, offline) — this module is the I/O half; every store's merge
// semantics (monotonic — never move a timestamp backwards) live in each
// store file itself, next to the localStorage shape it already owns.
//
// Table: public.user_notice_receipts (user_id, tenant_id, notice_key,
// announced_at, acked_at, snoozed_until, updated_at), PK (user_id, tenant_id,
// notice_key), RLS = own rows in a tenant you're an active member of, in the
// supabase_realtime publication.

import { supabase } from '../supabase';
import { withTimeout } from '../utils/async/withTimeout';
import { maxIso, isNewerIso } from '../utils/notices/noticeReceiptMath';

// Re-exported for backward compatibility — the pure math lives in
// `noticeReceiptMath.js` (no `../supabase` import) so store files that need
// ONLY these can avoid pulling the real supabase client into their tests.
export { maxIso, isNewerIso };

// Hydration window — applied ONLY to `estimate_response:<id>` rows, whose
// source is itself bounded (the summary/badge never look past 30 days, so an
// older ack can never matter). Every OTHER store's receipt must hydrate no
// matter how old it is (audit 2026-09-26, class C): a worker's `assigned:<id>`
// baseline is written ONCE and never touched again, so a 180-day window on
// `updated_at` dropped it on a new device and — as soon as ANY newer
// `assigned:` row existed — every older customer re-notified as "new on your
// route"; an open delivery issue announced >180 days ago re-greeted; the
// singleton `office_inbox_seen` / `estimate_response_seen` stamps fell out.
const WINDOWED_LOOKBACK_DAYS = 180;
const WINDOWED_KEY_PATTERN = 'estimate_response:%';
// Page size MUST be ≤ PostgREST `max_rows` (supabase/config.toml: 1000). The
// old single `.limit(2000)` was silently capped at 1000 by the server, and it
// cut the OLDEST rows (updated_at DESC) — exactly the baseline receipts above.
// Pages walk the PK order (notice_key), so a row updated mid-walk cannot
// shift the offsets under us. Guard: noticeReceipts.test.js reads max_rows.
export const NOTICE_RECEIPTS_PAGE_SIZE = 1000;
const MAX_PAGES = 25;
const UPSERT_BATCH_SIZE = 200;

const SELECT_COLUMNS = 'user_id, tenant_id, notice_key, announced_at, acked_at, snoozed_until, updated_at';

/** Every page of one filtered query, or `{ data: [], error }` on ANY page
 *  failure — all-or-nothing, because a PARTIAL set is worse than none: the
 *  worker's "no `assigned:` receipt at all → silent baseline" rule reads a
 *  half-hydrated set as "these customers are new". */
async function fetchAllPages(buildQuery) {
  const rows = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const from = page * NOTICE_RECEIPTS_PAGE_SIZE;
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await withTimeout(buildQuery()
      .order('notice_key', { ascending: true })
      .range(from, from + NOTICE_RECEIPTS_PAGE_SIZE - 1));
    if (error) return { data: [], error };
    const batch = Array.isArray(data) ? data : [];
    rows.push(...batch);
    if (batch.length < NOTICE_RECEIPTS_PAGE_SIZE) return { data: rows, error: null };
  }
  // Partial is worse than empty: a truncated set re-surfaces old notices as
  // new (security review P2). Fail safe — greetings may repeat, never lie.
  console.warn('fetchNoticeReceipts: page ceiling reached — refusing a partial hydration');
  return { data: [], error: new Error('NOTICE_RECEIPTS_PAGE_CEILING') };
}

/**
 * @param {{ tenantId: string, userId: string }} args
 * @returns {Promise<{ data: Array, error: any }>}
 */
export async function fetchNoticeReceipts({ tenantId, userId } = {}) {
  if (!tenantId || !userId) return { data: [], error: null };
  const since = new Date(Date.now() - WINDOWED_LOOKBACK_DAYS * 24 * 60 * 60 * 1000).toISOString();
  const base = () => supabase
    .from('user_notice_receipts')
    .select(SELECT_COLUMNS)
    .eq('tenant_id', tenantId)
    .eq('user_id', userId);
  try {
    const [durable, windowed] = await Promise.all([
      fetchAllPages(() => base().not('notice_key', 'like', WINDOWED_KEY_PATTERN)),
      fetchAllPages(() => base().like('notice_key', WINDOWED_KEY_PATTERN).gt('updated_at', since)),
    ]);
    const error = durable.error || windowed.error;
    if (error) {
      console.warn('fetchNoticeReceipts failed:', error);
      return { data: [], error };
    }
    return { data: [...durable.data, ...windowed.data], error: null };
  } catch (err) {
    // withTimeout / a thrown network error — non-fatal, same as every other
    // greeting read in this app: a failed fetch must never block the UI.
    console.warn('fetchNoticeReceipts failed:', err);
    return { data: [], error: err };
  }
}

/**
 * Fire-and-forget, non-fatal, batched (≤200/request). Never called with no
 * userId/tenantId — every call site in the store files checks first via
 * `tenantIdForNoticeWrite`.
 *
 * @param {{ tenantId: string, userId: string, rows: Array<{ notice_key: string,
 *   announced_at?: string|null, acked_at?: string|null, snoozed_until?: string|null }> }} args
 */
export function upsertNoticeReceipts({ tenantId, userId, rows } = {}) {
  if (!tenantId || !userId || !Array.isArray(rows) || rows.length === 0) return;
  const nowIso = new Date().toISOString();
  const payload = rows
    .filter((r) => r && typeof r.notice_key === 'string' && r.notice_key.length > 0 && r.notice_key.length <= 200)
    .map((r) => ({
      user_id: userId,
      tenant_id: tenantId,
      notice_key: r.notice_key,
      announced_at: r.announced_at || null,
      acked_at: r.acked_at || null,
      snoozed_until: r.snoozed_until || null,
      updated_at: nowIso,
    }));
  if (payload.length === 0) return;

  const send = async () => {
    for (let i = 0; i < payload.length; i += UPSERT_BATCH_SIZE) {
      const batch = payload.slice(i, i + UPSERT_BATCH_SIZE);
      try {
        // eslint-disable-next-line no-await-in-loop
        const { error } = await withTimeout(supabase
          .from('user_notice_receipts')
          .upsert(batch, { onConflict: 'user_id,tenant_id,notice_key' }));
        if (error) console.warn('upsertNoticeReceipts failed:', error);
      } catch (err) {
        console.warn('upsertNoticeReceipts failed:', err);
      }
    }
  };
  // Fire-and-forget: the caller's local write already happened and is the
  // truth for this device (data-freshness doctrine). A rejection here must
  // never surface — `send()` already catches per-batch, this is belt/braces.
  void send().catch((err) => console.warn('upsertNoticeReceipts failed:', err));
}
